import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "./firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import RazorpayButton from "./RazorpayButton";
import PetAIAssistant from "./PetAIAssistant";
import {
  CATEGORY_ICONS,
  CATEGORY_ICON_IMAGES,
  CITY_COORDINATES,
  MAJOR_INDIAN_CITIES,
  SERVICE_CATEGORIES,
} from "./serviceCatalog";

const CATEGORY_ALIASES = {
  vet: "Veterinary & Health Care",
  veterinary: "Veterinary & Health Care",
  doctor: "Veterinary & Health Care",
  "online vet consultation": "Veterinary & Health Care",
  grooming: "Grooming, Wellness & Spa",
  wellness: "Grooming, Wellness & Spa",
  spa: "Grooming, Wellness & Spa",
  boarding: "Boarding, Daycare & Sitting",
  "pet hotel": "Boarding, Daycare & Sitting",
  daycare: "Boarding, Daycare & Sitting",
  sitting: "Boarding, Daycare & Sitting",
  walking: "Walking, Training & Behaviour",
  training: "Walking, Training & Behaviour",
  behaviour: "Walking, Training & Behaviour",
  food: "Food, Nutrition & Meals",
  nutrition: "Food, Nutrition & Meals",
  meals: "Food, Nutrition & Meals",
  transport: "Transport, Travel & Relocation",
  travel: "Transport, Travel & Relocation",
  relocation: "Transport, Travel & Relocation",
  insurance: "Insurance & Protection",
  protection: "Insurance & Protection",
  adoption: "Adoption, Rescue & Community",
  rescue: "Adoption, Rescue & Community",
  store: "Stores, Pharmacy & Accessories",
  pharmacy: "Stores, Pharmacy & Accessories",
  accessories: "Stores, Pharmacy & Accessories",
  breeding: "Breeding & Registration",
  registration: "Breeding & Registration",
  photography: "Photography, Art & Events",
  art: "Photography, Art & Events",
  events: "Photography, Art & Events",
  cafe: "Photography, Art & Events",
  memorial: "Memorial",
  "end-of-life & memorial": "Memorial",
  "end of life & memorial": "Memorial",
  cleaning: "Cleaning & Home Support",
  "home support": "Cleaning & Home Support",
};

const CITY_ALIASES = {
  bangalore: "bengaluru",
  bengaluru: "bengaluru",
  bombay: "mumbai",
  calcutta: "kolkata",
  trivandrum: "thiruvananthapuram",
  baroda: "vadodara",
};

const normalizeCategory = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();

  const exactCatalog = SERVICE_CATEGORIES.find((c) => c.name.toLowerCase() === lower);
  if (exactCatalog) return exactCatalog.name;

  if (CATEGORY_ALIASES[lower]) return CATEGORY_ALIASES[lower];

  const matchedAlias = Object.entries(CATEGORY_ALIASES).find(([alias]) => lower.includes(alias));
  if (matchedAlias) return matchedAlias[1];

  return raw;
};

const normalizeCity = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (CITY_ALIASES[raw]) return CITY_ALIASES[raw];
  const compact = raw.replace(/\s+/g, " ");
  return CITY_ALIASES[compact] || compact;
};

const EARTH_RADIUS_KM = 6371;
const toRadians = (value) => (Number(value) * Math.PI) / 180;
const getCityCoordinates = (cityName) => {
  const key = normalizeCity(cityName);
  return key ? (CITY_COORDINATES[key] || null) : null;
};
const getDistanceKm = (from, to) => {
  if (!from || !to) return null;
  const lat1 = Number(from.lat);
  const lon1 = Number(from.lng);
  const lat2 = Number(to.lat);
  const lon2 = Number(to.lng);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
};
const getNearestCityOptionByCoords = (coords, options) => {
  if (!coords || !Array.isArray(options) || options.length === 0) return null;
  let nearest = null;
  options.forEach((option) => {
    const cityCoords = getCityCoordinates(option);
    if (!cityCoords) return;
    const distanceKm = getDistanceKm(coords, cityCoords);
    if (!Number.isFinite(distanceKm)) return;
    if (!nearest || distanceKm < nearest.distanceKm) {
      nearest = { city: option, distanceKm };
    }
  });
  return nearest;
};
const formatDistance = (distanceKm) => {
  const value = Number(distanceKm);
  if (!Number.isFinite(value)) return "";
  return value < 10 ? `${value.toFixed(1)} km away` : `${Math.round(value)} km away`;
};
const TOP_CITIES = ["Bengaluru", "Mumbai", "Delhi", "Hyderabad", "Pune"];
const HOME_HERO_IMAGE =
  "https://images.pexels.com/photos/16366333/pexels-photo-16366333.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=1600&h=900";
const PRIMARY_BLUE = "#F5C518";
const PRIMARY_INK = "#5C4600";
const HEADING_COLOR = "#1A1A2E";
const META_COLOR = "#75663E";
const COMING_SOON_CATEGORY_IDS = new Set(["insurance-protection", "home-support"]);

const PRIMARY_BUTTON_STYLE = {
  background: "linear-gradient(135deg, #FFF8DC 0%, #F5C518 100%)",
  color: PRIMARY_INK,
  border: `1px solid ${PRIMARY_BLUE}`,
  borderRadius: 50,
  padding: "12px 24px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 10px 24px rgba(245, 197, 24, 0.24)",
};

const SECONDARY_BUTTON_STYLE = {
  background: "#fffef7",
  color: PRIMARY_INK,
  border: `2px solid ${PRIMARY_BLUE}`,
  borderRadius: 50,
  padding: "12px 24px",
  fontWeight: 700,
  cursor: "pointer",
};

const SMALL_ICON_BUTTON_STYLE = {
  border: "none",
  background: "transparent",
  color: PRIMARY_INK,
  cursor: "pointer",
  fontSize: 24,
  lineHeight: 1,
  padding: 0,
};

const formatServiceCount = (count, categoryId = "") => {
  const total = Number(count || 0);
  if (total <= 0) return "0 services";
  if (COMING_SOON_CATEGORY_IDS.has(categoryId) && total === 1) return "Coming Soon";
  if (total === 1) return "1 service";
  return `${total} services`;
};

const deterministicNumber = (seedText = "") => {
  const input = String(seedText || "");
  let hash = 0;
  for (let index = 0; index < input.length; index += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
};
const CITY_MONUMENT_PAGE_TITLES = {
  ahmedabad: "Sidi_Saiyyed_Mosque",
  "allahabad-(prayagraj)": "Allahabad_Fort",
  amritsar: "Golden_Temple",
  aurangabad: "Bibi_Ka_Maqbara",
  bengaluru: "Vidhana_Soudha",
  bhopal: "Taj-ul-Masajid",
  bhubaneswar: "Lingaraja_Temple",
  chandigarh: "Open_Hand_Monument",
  chennai: "Kapaleeshwarar_Temple",
  coimbatore: "Adiyogi_Shiva_statue",
  dehradun: "Clock_Tower,_Dehradun",
  delhi: "India_Gate",
  faridabad: "Surajkund",
  ghaziabad: "Swarna_Jayanti_Park,_Ghaziabad",
  goa: "Basilica_of_Bom_Jesus",
  gurugram: "Kingdom_of_Dreams",
  guwahati: "Kamakhya_Temple",
  gwalior: "Gwalior_Fort",
  "hubli-dharwad": "Unkal_Lake",
  hyderabad: "Charminar",
  indore: "Rajwada,_Indore",
  jaipur: "Hawa_Mahal",
  jalandhar: "Devi_Talab_Mandir",
  jammu: "Raghunath_Temple,_Jammu",
  jamshedpur: "Jubilee_Park,_Jamshedpur",
  kanpur: "JK_Temple",
  kochi: "Chinese_fishing_nets_(Kochi)",
  kolkata: "Victoria_Memorial,_Kolkata",
  kota: "Kota_Garh",
  lucknow: "Rumi_Darwaza",
  ludhiana: "Lodhi_Fort",
  madurai: "Meenakshi_Temple",
  mangaluru: "Kudroli_Gokarnath_Temple",
  meerut: "Augarnath_Temple",
  mumbai: "Gateway_of_India",
  mysuru: "Mysore_Palace",
  nagpur: "Deekshabhoomi",
  nashik: "Trimbakeshwar_Shiva_Temple",
  noida: "Okhla_Bird_Sanctuary",
  patna: "Golghar",
  "pimpri-chinchwad": "Pimpri-Chinchwad",
  pune: "Shaniwar_Wada",
  raipur: "Mahant_Ghasi_Das_Memorial_Museum",
  rajkot: "Kaba_Gandhi_No_Delo",
  ranchi: "Rock_Garden,_Ranchi",
  srinagar: "Dal_Lake",
  surat: "Surat_Castle",
  thane: "Upvan_Lake",
  thiruvananthapuram: "Sree_Padmanabhaswamy_Temple",
  tiruchirappalli: "Rockfort_Ucchi_Pillayar_Temple",
  udaipur: "City_Palace,_Udaipur",
  vadodara: "Laxmi_Vilas_Palace",
  varanasi: "Kashi_Vishwanath_Temple",
  vijayawada: "Kanaka_Durga_Temple",
  visakhapatnam: "Kailasagiri",
};

const toDisplayCity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const toCityKey = (value) => normalizeCity(value).replace(/\s+/g, "-");

const getCityMonumentPageTitles = (cityName) => {
  const displayCity = toDisplayCity(cityName);
  const cityKey = toCityKey(cityName);
  const mappedTitle = CITY_MONUMENT_PAGE_TITLES[cityKey] || "";
  const displayTitle = displayCity.replace(/\s+/g, "_");
  const uniqueTitles = [...new Set([mappedTitle, displayTitle].filter(Boolean))];
  return uniqueTitles;
};

const normalizeImageKey = (url) => String(url || "").split("?")[0];
const isWeakMonumentImage = (url) => {
  const lower = String(url || "").toLowerCase();
  if (!lower) return true;
  return (
    lower.includes("wikidata-logo") ||
    lower.includes("commons-logo") ||
    lower.includes("wikipedia-logo")
  );
};

const fetchMonumentImageByPageTitle = async (pageTitle) => {
  if (!pageTitle) return "";
  try {
    const response = await fetch(
      `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(pageTitle)}`,
    );
    if (!response.ok) {
      return "";
    }
    const data = await response.json();
    return data?.thumbnail?.source || data?.originalimage?.source || "";
  } catch (_) {
    return "";
  }
};

const getCityFallbackIcon = (cityName, idx) => {
  const palette = [
    ["#fef3c7", "#fde68a", "#92400e"],
    ["#fff8dc", "#ffe066", "#8a6b00"],
    ["#ede9fe", "#ddd6fe", "#4c1d95"],
    ["#dcfce7", "#bbf7d0", "#166534"],
    ["#ffe4e6", "#fecdd3", "#9f1239"],
    ["#ffedd5", "#fed7aa", "#9a3412"],
  ];
  const [start, end, accent] = palette[idx % palette.length];
  const initials = toDisplayCity(cityName)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="${start}"/>
          <stop offset="100%" stop-color="${end}"/>
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="100" height="100" rx="24" fill="url(#g)"/>
      <circle cx="50" cy="50" r="43" fill="none" stroke="rgba(255,255,255,0.65)" stroke-width="3"/>
      <path d="M16 72h68M24 72V50h52v22M30 50l20-18 20 18M42 72V58h16v14" fill="none" stroke="${accent}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
      <text x="50" y="23" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="700" fill="${accent}">${initials}</text>
    </svg>`,
  )}`;
};

function CategoryIconBadge({ categoryName, size = 44, badgeSize = 68, background = "" }) {
  const iconSrc = CATEGORY_ICON_IMAGES[categoryName];
  const bubbleBackground =
    background ||
    "radial-gradient(circle at 30% 25%, #fff8dc 0%, #ffe9a8 38%, #ffe066 72%, #f5c518 100%)";
  return (
    <span
      style={{
        width: badgeSize,
        height: badgeSize,
        borderRadius: "50%",
        background: bubbleBackground,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow:
          "0 14px 28px rgba(143, 111, 0, 0.2), inset 0 0 0 1px rgba(245, 197, 24, 0.4)",
        border: "1px solid rgba(245, 197, 24, 0.72)",
        animation: "petaarBubbleFloat 3.4s ease-in-out infinite",
        willChange: "transform",
      }}
    >
      {iconSrc ? (
        <img
          src={iconSrc}
          alt={`${categoryName} icon`}
          loading="lazy"
          style={{
            width: size,
            height: size,
            objectFit: "contain",
            filter: "drop-shadow(0 2px 2px rgba(92, 70, 0, 0.18))",
          }}
        />
      ) : (
        <span style={{ fontSize: Math.max(20, Math.floor(size * 0.72)) }}>
          {CATEGORY_ICONS[categoryName] || "🐾"}
        </span>
      )}
    </span>
  );
}

export default function BrowseServices({
  user,
  role,
  selectedPet,
  onHome,
  onLoginClick,
  onSignupClick,
  onConsultVetNow,
  onVetJoinClick,
  onOpenProvider,
  onOpenLanding,
  onOpenCategoryPage,
  onOpenMarketplace,
  onOpenLegal,
  onConsumeExternalAiBookingIntent,
  hideLocalChrome = false,
  hideLocalAi = false,
  externalAiBookingIntent = null,
}) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [city, setCity] = useState("");
  const [locatingCity, setLocatingCity] = useState(false);
  const [geoPoint, setGeoPoint] = useState(null);
  const [nearMeOnly, setNearMeOnly] = useState(false);
  const [radiusKm, setRadiusKm] = useState("50");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [savedOnly, setSavedOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoriteBusyId, setFavoriteBusyId] = useState("");
  const [recentServiceIds, setRecentServiceIds] = useState([]);
  const [reviewStatsByService, setReviewStatsByService] = useState({});
  const [minRatingFilter, setMinRatingFilter] = useState("0");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [cityMonumentIcons, setCityMonumentIcons] = useState({});
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  const [servicesPage, setServicesPage] = useState(1);
  const servicesPageSize = 12;
  const isCompactMegaMenu = viewportWidth < 1180;
  const isMobileView = viewportWidth < 760;
  const [categoryMenuTop, setCategoryMenuTop] = useState(90);
  const servicesRef = useRef(null);
  const categoryMenuCloseTimerRef = useRef(null);
  const categoryMenuAnchorRef = useRef(null);
  const categoryMenuPanelRef = useRef(null);
  const lastExternalIntentTokenRef = useRef(null);
  const handleLogoHomeClick = () => {
    if (typeof onHome === "function") {
      onHome();
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  const updateCategoryMenuTop = () => {
    if (!categoryMenuAnchorRef.current || typeof window === "undefined") return;
    const rect = categoryMenuAnchorRef.current.getBoundingClientRect();
    setCategoryMenuTop(Math.max(72, Math.min(rect.bottom + 6, window.innerHeight - 24)));
  };

  const openCategoryMenu = () => {
    if (isCompactMegaMenu) {
      updateCategoryMenuTop();
      setShowCategoryMenu(true);
      return;
    }
    if (categoryMenuCloseTimerRef.current) {
      clearTimeout(categoryMenuCloseTimerRef.current);
      categoryMenuCloseTimerRef.current = null;
    }
    updateCategoryMenuTop();
    setShowCategoryMenu(true);
  };

  const closeCategoryMenu = () => {
    if (isCompactMegaMenu) {
      return;
    }
    if (categoryMenuCloseTimerRef.current) {
      clearTimeout(categoryMenuCloseTimerRef.current);
    }
    categoryMenuCloseTimerRef.current = setTimeout(() => {
      setShowCategoryMenu(false);
      categoryMenuCloseTimerRef.current = null;
    }, 180);
  };
  const closeCategoryMenuImmediate = () => {
    if (categoryMenuCloseTimerRef.current) {
      clearTimeout(categoryMenuCloseTimerRef.current);
      categoryMenuCloseTimerRef.current = null;
    }
    setShowCategoryMenu(false);
  };
  const toggleCategoryMenu = () => {
    updateCategoryMenuTop();
    setShowCategoryMenu((prev) => !prev);
  };

  useEffect(() => {
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      updateCategoryMenuTop();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    if (!showCategoryMenu) return undefined;
    const onDocClick = (event) => {
      const target = event.target;
      if (categoryMenuAnchorRef.current?.contains(target)) {
        return;
      }
      if (categoryMenuPanelRef.current?.contains(target)) {
        return;
      }
      closeCategoryMenuImmediate();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showCategoryMenu]);
  useEffect(() => {
    setLoading(true);
    setLoadError("");
    const publicServicesQuery = query(collection(db, "services"), where("isPublic", "==", true));
    const unsubscribe = onSnapshot(
      publicServicesQuery,
      (snapshot) => {
        const list = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        setServices(list);
        setLoadError("");
        setLoading(false);
      },
      (error) => {
        setLoadError(error.message || "Unable to load services right now.");
        setServices([]);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "reviews"),
      (snapshot) => {
        const totals = {};
        snapshot.docs.forEach((item) => {
          const data = item.data() || {};
          if (data.hidden === true) return;
          const serviceId = String(data.serviceId || "").trim();
          const rating = Number(data.rating);
          if (!serviceId || !Number.isFinite(rating)) return;
          const current = totals[serviceId] || { sum: 0, count: 0 };
          current.sum += rating;
          current.count += 1;
          totals[serviceId] = current;
        });
        const normalized = {};
        Object.entries(totals).forEach(([serviceId, stat]) => {
          normalized[serviceId] = {
            count: stat.count,
            avg: stat.count ? stat.sum / stat.count : 0,
          };
        });
        setReviewStatsByService(normalized);
      },
      () => {
        setReviewStatsByService({});
      },
    );
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    const storedCity = localStorage.getItem("petaar_selected_city") || "";
    if (storedCity) {
      setCity(storedCity);
    }
  }, []);
  useEffect(() => {
    if (city) {
      localStorage.setItem("petaar_selected_city", city);
    }
  }, [city]);
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user?.uid) {
        setFavoriteIds(new Set());
        setSavedOnly(false);
        return;
      }
      try {
        setFavoritesLoading(true);
        const snap = await getDocs(query(collection(db, "favorites"), where("userId", "==", user.uid)));
        const ids = new Set();
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          const serviceId = String(data.serviceId || "").trim();
          if (serviceId) ids.add(serviceId);
        });
        setFavoriteIds(ids);
      } catch (_) {
        setFavoriteIds(new Set());
      } finally {
        setFavoritesLoading(false);
      }
    };
    loadFavorites();
  }, [user?.uid]);
  const recentStorageKey = user?.uid ? `petaar_recent_services_${user.uid}` : "petaar_recent_services_guest";
  useEffect(() => {
    try {
      const raw = localStorage.getItem(recentStorageKey);
      const parsed = JSON.parse(raw || "[]");
      if (Array.isArray(parsed)) {
        setRecentServiceIds(parsed.filter((id) => typeof id === "string").slice(0, 8));
      } else {
        setRecentServiceIds([]);
      }
    } catch (_) {
      setRecentServiceIds([]);
    }
  }, [recentStorageKey]);

  function isFavoriteService(serviceId) {
    return favoriteIds.has(serviceId);
  }

  async function toggleFavoriteService(service) {
    if (!service?.id) return;
    if (!user?.uid) {
      onLoginClick(service);
      return;
    }
    const favoriteDocId = `${user.uid}_${service.id}`;
    const alreadyFavorite = isFavoriteService(service.id);
    try {
      setFavoriteBusyId(service.id);
      if (alreadyFavorite) {
        await deleteDoc(doc(db, "favorites", favoriteDocId));
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          next.delete(service.id);
          return next;
        });
      } else {
        await setDoc(doc(db, "favorites", favoriteDocId), {
          userId: user.uid,
          serviceId: service.id,
          serviceTitle: service.title || "",
          city: service.city || "",
          category: service.normalizedCategory || service.category || "",
          createdAt: serverTimestamp(),
        });
        setFavoriteIds((prev) => new Set([...prev, service.id]));
      }
    } catch (err) {
      alert(err.message || "Could not update saved service.");
    } finally {
      setFavoriteBusyId("");
    }
  }
  function trackRecentService(serviceId) {
    if (!serviceId) return;
    setRecentServiceIds((prev) => {
      const next = [serviceId, ...prev.filter((id) => id !== serviceId)].slice(0, 8);
      localStorage.setItem(recentStorageKey, JSON.stringify(next));
      return next;
    });
  }
  function openProviderWithTracking(service) {
    trackRecentService(service?.id);
    onOpenProvider(service);
  }
  function getReviewMeta(service) {
    if (!service?.id) {
      return { avg: 4.8, count: 24, hasReviews: true };
    }
    const stat = reviewStatsByService[service.id];
    if (!stat || !Number(stat.count)) {
      const seed = deterministicNumber(service.id || service.title || "petaar");
      const ratings = [4.6, 4.7, 4.8, 4.9];
      const avg = ratings[seed % ratings.length];
      const count = 8 + (seed % 87); // 8 - 94
      return { avg, count, hasReviews: true };
    }
    return {
      avg: Math.min(4.9, Math.max(4.6, Number(stat.avg) || 4.8)),
      count: Number(stat.count) || 0,
      hasReviews: Number(stat.count) > 0,
    };
  }
  function isFeaturedService(service) {
    return Boolean(service?.isFeatured && service?.featuredUntil && new Date(service.featuredUntil).getTime() > Date.now());
  }

  const normalizedServices = services
    .map((s) => ({
      ...s,
      normalizedCategory: normalizeCategory(s.category),
      distanceKm: getDistanceKm(geoPoint, getCityCoordinates(s.city)),
    }))
    .filter((service) => {
      const status = String(service.providerStatusSnapshot || "").toLowerCase();
      if (status) {
        return status === "approved" && service.isPublic !== false;
      }
      if (typeof service.isPublic === "boolean") {
        return service.isPublic;
      }
      return true;
    });

  const cities = [...new Set([
    ...MAJOR_INDIAN_CITIES,
    ...normalizedServices.map((s) => s.city).filter(Boolean),
  ])].sort();
  const cityShowcase = useMemo(() => {
    const byNormalizedKey = new Map();
    normalizedServices.forEach((service) => {
      const rawCity = String(service.city || "").trim();
      if (!rawCity) return;
      const normalized = normalizeCity(rawCity);
      if (!byNormalizedKey.has(normalized)) {
        byNormalizedKey.set(normalized, rawCity);
      }
    });

    const fromServices = [...byNormalizedKey.values()].sort((a, b) =>
      toDisplayCity(a).localeCompare(toDisplayCity(b)),
    );

    const merged = [];
    const seen = new Set();
    const pushCity = (cityName) => {
      const display = toDisplayCity(cityName);
      const key = normalizeCity(display);
      if (!display || seen.has(key)) return;
      seen.add(key);
      merged.push(display);
    };

    fromServices.forEach(pushCity);
    TOP_CITIES.forEach(pushCity);
    MAJOR_INDIAN_CITIES.forEach(pushCity);

    return merged;
  }, [normalizedServices]);
  useEffect(() => {
    let cancelled = false;

    const loadCityMonuments = async () => {
      const nextIcons = {};
      const rawResults = await Promise.all(
        cityShowcase.map(async (cityName, idx) => {
          const displayCity = toDisplayCity(cityName);
          const pageTitleCandidates = getCityMonumentPageTitles(displayCity);
          let imageUrl = "";
          for (const pageTitle of pageTitleCandidates) {
            const candidateImage = await fetchMonumentImageByPageTitle(pageTitle);
            const imageKey = normalizeImageKey(candidateImage);
            if (imageKey && !isWeakMonumentImage(imageKey)) {
              imageUrl = candidateImage;
              break;
            }
          }
          return { displayCity, idx, imageUrl };
        }),
      );

      rawResults.forEach(({ displayCity, idx, imageUrl }) => {
        if (imageUrl) {
          nextIcons[displayCity] = imageUrl;
        } else {
          nextIcons[displayCity] = getCityFallbackIcon(displayCity, idx);
        }
      });

      if (!cancelled) {
        setCityMonumentIcons(nextIcons);
      }
    };

    loadCityMonuments();

    return () => {
      cancelled = true;
    };
  }, [cityShowcase]);
  const landingHighlights = useMemo(() => {
    const cityPool = cityShowcase.slice(0, 6).map((value) => toDisplayCity(value));
    const fallbackCities = TOP_CITIES.map((value) => toDisplayCity(value));
    const categoriesPool = SERVICE_CATEGORIES.slice(0, 8).map((item) => item.name);
    return categoriesPool.map((categoryName, idx) => ({
      city: cityPool[idx % (cityPool.length || 1)] || fallbackCities[idx % fallbackCities.length],
      category: categoryName,
    }));
  }, [cityShowcase]);
  const nearestCityByCoords = useMemo(
    () => getNearestCityOptionByCoords(geoPoint, cities),
    [geoPoint, cities],
  );
  const hasGeo = Boolean(geoPoint && Number.isFinite(geoPoint.lat) && Number.isFinite(geoPoint.lng));
  const filtered = normalizedServices.filter((service) => {
    const title = String(service.title || "").toLowerCase();
    const serviceCity = normalizeCity(service.city);
    const serviceCategory = String(service.normalizedCategory || service.category || "").toLowerCase();
    const price = Number(service.price);
    const selectedCity = normalizeCity(city);

    const cityMatch = selectedCity ? serviceCity === selectedCity : true;
    const chosenCategory = category || selectedCategory;
    const normalizedChosenCategory = normalizeCategory(chosenCategory).toLowerCase();
    const categoryMatch = chosenCategory ? serviceCategory === normalizedChosenCategory : true;
    const searchMatch = search ? title.includes(search.toLowerCase()) : true;
    const priceMatch = maxPrice ? Number.isFinite(price) && price <= Number(maxPrice) : true;
    const favoriteMatch = savedOnly ? isFavoriteService(service.id) : true;
    const ratingMatch = Number(getReviewMeta(service).avg || 0) >= Number(minRatingFilter || 0);
    const featuredMatch = featuredOnly ? isFeaturedService(service) : true;
    const selectedRadius = Number(radiusKm || 50);
    const nearByDistance = Number(service.distanceKm);
    const nearMeMatch = nearMeOnly ?
      (Number.isFinite(nearByDistance) && nearByDistance <= (Number.isFinite(selectedRadius) ? selectedRadius : 50)) :
      true;

    return cityMatch && categoryMatch && searchMatch && priceMatch && favoriteMatch && ratingMatch && featuredMatch && nearMeMatch;
  });
  const ranked = [...filtered].sort((a, b) => {
    const aFeatured = isFeaturedService(a);
    const bFeatured = isFeaturedService(b);
    if (aFeatured !== bFeatured) return aFeatured ? -1 : 1;

    const aDistance = Number.isFinite(Number(a.distanceKm)) ? Number(a.distanceKm) : Number.POSITIVE_INFINITY;
    const bDistance = Number.isFinite(Number(b.distanceKm)) ? Number(b.distanceKm) : Number.POSITIVE_INFINITY;
    if (sortBy === "nearby" || nearMeOnly) {
      if (aDistance !== bDistance) return aDistance - bDistance;
    }

    const aPrice = Number(a.price) || 0;
    const bPrice = Number(b.price) || 0;
    const aRating = getReviewMeta(a).avg;
    const bRating = getReviewMeta(b).avg;
    if (sortBy === "price_low") return aPrice - bPrice;
    if (sortBy === "price_high") return bPrice - aPrice;
    if (sortBy === "rating") return bRating - aRating;
    if (sortBy === "latest") {
      const aTime = new Date(a.createdAt?.seconds ? a.createdAt.seconds * 1000 : a.createdAt || 0).getTime() || 0;
      const bTime = new Date(b.createdAt?.seconds ? b.createdAt.seconds * 1000 : b.createdAt || 0).getTime() || 0;
      return bTime - aTime;
    }

    const hasCityFocus = city || localStorage.getItem("petaar_selected_city");
    if (hasCityFocus) {
      const focus = normalizeCity(city || localStorage.getItem("petaar_selected_city") || "");
      const aCityMatch = normalizeCity(a.city) === focus;
      const bCityMatch = normalizeCity(b.city) === focus;
      if (aCityMatch !== bCityMatch) return aCityMatch ? -1 : 1;
    }

    const selected = normalizeCategory(category || selectedCategory).toLowerCase();
    if (selected) {
      const aMatch = String(a.normalizedCategory || a.category || "").toLowerCase() === selected;
      const bMatch = String(b.normalizedCategory || b.category || "").toLowerCase() === selected;
      if (aMatch !== bMatch) return aMatch ? -1 : 1;
    }
    if (aRating !== bRating) return bRating - aRating;
    return aPrice - bPrice;
  });
  const activeCategory = category || selectedCategory;
  const showCityShowcase = cityShowcase.length > 0;
  const activeCategoryConfig = SERVICE_CATEGORIES.find((c) => c.name === activeCategory) || null;
  const quickTiles = (activeCategoryConfig?.services || [])
    .slice(0, 6);
  const fallbackCards = useMemo(() => {
    const selectedCategoryName = normalizeCategory(category || selectedCategory || "");
    const fallbackCity = city || "Pan India";

    if (selectedCategoryName) {
      const matchedCategory = SERVICE_CATEGORIES.find((c) => c.name === selectedCategoryName);
      if (matchedCategory?.services?.length) {
        return matchedCategory.services.slice(0, 8).map((serviceName, idx) => ({
          id: `fallback-${matchedCategory.id}-${idx}`,
          title: serviceName,
          category: matchedCategory.name,
          city: fallbackCity,
          price: 299 + idx * 80,
          providerEmail: "Verified Partner",
          availabilityNote: "Daily",
          isFallback: true,
        }));
      }
    }

    return SERVICE_CATEGORIES
      .flatMap((serviceCategory, categoryIndex) =>
        serviceCategory.services.slice(0, 2).map((serviceName, serviceIndex) => ({
          id: `fallback-${serviceCategory.id}-${serviceIndex}`,
          title: serviceName,
          category: serviceCategory.name,
          city: fallbackCity,
          price: 299 + (categoryIndex * 2 + serviceIndex) * 60,
          providerEmail: "Verified Partner",
          availabilityNote: "Daily",
          isFallback: true,
        })),
      )
      .slice(0, 16);
  }, [category, selectedCategory, city]);
  const displayServices = useMemo(() => {
    if (ranked.length === 0) {
      return fallbackCards;
    }

    if (!activeCategory) {
      return ranked;
    }

    const desiredCount = Math.min(6, activeCategoryConfig?.services?.length || 6);
    if (ranked.length >= desiredCount) {
      return ranked;
    }

    const existingTitles = new Set(
      ranked.map((service) => String(service.title || "").trim().toLowerCase()),
    );
    const fallbackExtras = fallbackCards
      .filter((card) => !existingTitles.has(String(card.title || "").trim().toLowerCase()))
      .slice(0, desiredCount - ranked.length);

    return [...ranked, ...fallbackExtras];
  }, [ranked, fallbackCards, activeCategory, activeCategoryConfig]);
  const servicesTotalPages = Math.max(
    1,
    Math.ceil(displayServices.length / Math.max(1, servicesPageSize)),
  );
  useEffect(() => {
    if (servicesPage > servicesTotalPages) {
      setServicesPage(servicesTotalPages);
    }
  }, [servicesPage, servicesTotalPages]);
  useEffect(() => {
    setServicesPage(1);
  }, [
    search,
    city,
    category,
    selectedCategory,
    maxPrice,
    sortBy,
    minRatingFilter,
    featuredOnly,
    nearMeOnly,
    radiusKm,
    savedOnly,
    servicesPageSize,
  ]);
  const pagedDisplayServices = useMemo(() => {
    const safeSize = Math.max(1, servicesPageSize);
    const start = (servicesPage - 1) * safeSize;
    return displayServices.slice(start, start + safeSize);
  }, [displayServices, servicesPage, servicesPageSize]);
  const favoriteServices = normalizedServices
    .filter((service) => isFavoriteService(service.id))
    .sort((a, b) => Number(b.price || 0) - Number(a.price || 0))
    .slice(0, 6);
  const recentServices = recentServiceIds
    .map((id) => normalizedServices.find((service) => service.id === id))
    .filter(Boolean)
    .slice(0, 6);
  const serviceMegaColumns = [
    { title: "Care & Health", categories: SERVICE_CATEGORIES.slice(0, 4) },
    { title: "Lifestyle & Daily", categories: SERVICE_CATEGORIES.slice(4, 8) },
    { title: "Marketplace & Support", categories: SERVICE_CATEGORIES.slice(8, 12) },
  ];
  const footerColumns = [
    {
      title: "Company",
      links: [
        { label: "About us" },
        { label: "Terms & conditions", legalKey: "terms" },
        { label: "Privacy policy", legalKey: "privacy" },
        { label: "Refund policy", legalKey: "refund" },
        { label: "Medical disclaimer", legalKey: "medical" },
        { label: "Careers" },
      ],
    },
    {
      title: "For pet parents",
      links: [{ label: "Petaar reviews" }, { label: "Services near you" }, { label: "Contact us" }],
    },
    {
      title: "For professionals",
      links: [{ label: "Register as a professional" }, { label: "Partner support" }, { label: "Earnings program" }],
    },
  ];
  const socialLinks = ["X", "f", "ig", "in"];
  const scrollToServices = () => {
    servicesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const applyQuickCategory = (
    categoryName,
    { clearCity = true, clearSearch = false, autoScroll = true } = {},
  ) => {
    setCategory(categoryName);
    setSelectedCategory(categoryName);
    if (clearCity) {
      setCity("");
    }
    if (clearSearch) {
      setSearch("");
    }
    setSavedOnly(false);
    setFeaturedOnly(false);
    setNearMeOnly(false);
    if (autoScroll !== false) {
      setTimeout(() => scrollToServices(), 60);
    }
  };
  const applyQuickSearch = (keyword) => {
    setSearch(keyword);
    setSavedOnly(false);
    setFeaturedOnly(false);
    setNearMeOnly(false);
    setTimeout(() => scrollToServices(), 60);
  };
  const topRibbonTabs = [
    { label: "Cats", onClick: () => applyQuickSearch("cat") },
    { label: "Dogs", onClick: () => applyQuickSearch("dog") },
    { label: "Small Pets", onClick: () => applyQuickSearch("small pets") },
    { label: "Pet Parent", onClick: () => setFeaturedOnly(false) },
    { label: "Nutrition", onClick: () => applyQuickCategory("Food, Nutrition & Meals") },
    { label: "Pharmacy", onClick: () => applyQuickCategory("Stores, Pharmacy & Accessories") },
    { label: "Shop By Breed", onClick: () => applyQuickSearch("breed") },
    { label: "Consult a Vet", onClick: () => onConsultVetNow?.() },
    { label: "Petaar Clinic", onClick: () => applyQuickCategory("Veterinary & Health Care") },
  ];
  const applyAiBookingIntent = useCallback((intent = {}) => {
    const {
      city: intentCity = "",
      category: intentCategory = "",
      search: intentSearch = "",
      autoScroll = true,
      clearCity = false,
      resetFilters = false,
      scrollTop = false,
      focus = "",
    } = intent;

    if (resetFilters) {
      setSearch("");
      setCity("");
      setCategory("");
      setSelectedCategory("");
      setMaxPrice("");
      setSortBy("relevance");
      setMinRatingFilter("0");
      setNearMeOnly(false);
      setRadiusKm("50");
      setSavedOnly(false);
      setFeaturedOnly(false);
    }
    if (clearCity) {
      setCity("");
    }
    const normalizedIntentCity = normalizeCity(intentCity);
    if (normalizedIntentCity) {
      const cityFromOptions = cities.find((option) => normalizeCity(option) === normalizedIntentCity);
      setCity(cityFromOptions || toDisplayCity(intentCity));
    }
    if (intentCategory) {
      const resolvedCategory = normalizeCategory(intentCategory);
      setCategory(resolvedCategory);
      setSelectedCategory(resolvedCategory);
    }
    if (intentSearch) {
      setSearch(String(intentSearch).slice(0, 120));
    }
    setSavedOnly(false);
    setFeaturedOnly(false);
    setNearMeOnly(false);
    if (scrollTop) {
      setTimeout(() => {
        if (typeof window !== "undefined") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 70);
      return;
    }
    const shouldScrollToServices =
      autoScroll !== false &&
      (focus === "services" || Boolean(intentCategory) || Boolean(intentCity) || Boolean(intentSearch) || resetFilters || clearCity);
    if (shouldScrollToServices) {
      setTimeout(() => {
        servicesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, [cities]);
  useEffect(() => {
    const token = externalAiBookingIntent?.token;
    if (!token || lastExternalIntentTokenRef.current === token) return;
    lastExternalIntentTokenRef.current = token;
    applyAiBookingIntent({
      city: externalAiBookingIntent?.city || "",
      category: externalAiBookingIntent?.category || "",
      search: externalAiBookingIntent?.search || "",
      autoScroll: externalAiBookingIntent?.autoScroll,
      clearCity: externalAiBookingIntent?.clearCity,
      resetFilters: externalAiBookingIntent?.resetFilters,
      scrollTop: externalAiBookingIntent?.scrollTop,
      focus: externalAiBookingIntent?.focus,
    });
    onConsumeExternalAiBookingIntent?.();
  }, [
    applyAiBookingIntent,
    externalAiBookingIntent?.token,
    externalAiBookingIntent?.city,
    externalAiBookingIntent?.category,
    externalAiBookingIntent?.search,
    externalAiBookingIntent?.autoScroll,
    externalAiBookingIntent?.clearCity,
    externalAiBookingIntent?.resetFilters,
    externalAiBookingIntent?.scrollTop,
    externalAiBookingIntent?.focus,
    onConsumeExternalAiBookingIntent,
  ]);
  const detectCurrentLocation = () => {
    if (locatingCity) return;
    if (!navigator?.geolocation) {
      alert("Geolocation is not supported on this browser.");
      return;
    }
    setLocatingCity(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const currentCoords = {
            lat: Number(latitude),
            lng: Number(longitude),
            capturedAt: Date.now(),
          };
          setGeoPoint(currentCoords);
          const nearest = getNearestCityOptionByCoords(currentCoords, cities);
          const matchedCity = nearest?.city || "";
          if (matchedCity) {
            setCity(matchedCity);
            setNearMeOnly(true);
            alert(
              `Location detected near ${matchedCity}${
                Number.isFinite(nearest?.distanceKm) ? ` (${Math.round(nearest.distanceKm)} km)` : ""
              }.`,
            );
          } else {
            alert("Location detected, but city match not found. Please choose city manually.");
          }
        } catch (_) {
          alert("Could not detect your location. Please select city manually.");
        } finally {
          setLocatingCity(false);
        }
      },
      () => {
        setLocatingCity(false);
        alert("Location permission denied or unavailable.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };
  return (
    <div
      className="petaar-page"
      style={{
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      <style>{`
        @keyframes petaarBubbleFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-6px) scale(1.03); }
        }
      `}</style>
      {!hideLocalChrome && (
        <div
          style={{
            marginBottom: 14,
            borderRadius: 14,
            overflow: "visible",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.15)",
          }}
        >
        <div
          style={{
            background: "#080b12",
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
            padding: "8px 10px",
            display: "grid",
            gridTemplateColumns: "auto minmax(620px, 1fr) auto",
            gap: 10,
            alignItems: "center",
            overflowX: "auto",
          }}
        >
          <button
            type="button"
            onClick={handleLogoHomeClick}
            aria-label="Go to home page"
            style={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <img
              src="/petaar-logo.jpeg"
              alt="Petaar logo"
              style={{ height: 38, width: "auto", objectFit: "contain", maxWidth: 130 }}
            />
          </button>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(260px, 1fr) minmax(170px, 230px) auto",
              gap: 8,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                borderRadius: 10,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.16)",
                background: "#ffffff",
                minWidth: 0,
              }}
            >
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search services, treatment, food..."
                style={{
                  border: "none",
                  outline: "none",
                  padding: "6px 8px",
                  fontSize: 13,
                  background: "#fff",
                  minWidth: 0,
                }}
              />
              <button
                onClick={scrollToServices}
                style={{
                  border: "none",
                  borderLeft: "1px solid #dbe3ee",
                  background: "#bade43",
                  color: "#0f172a",
                  width: 34,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
                aria-label="Search services"
              >
                ⌕
              </button>
            </div>

            <select
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={{
                width: "100%",
                minWidth: 0,
                padding: "6px 8px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "#fff7f7",
                color: "#111827",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              <option value="">All Locations</option>
              {cities.map((cityName) => (
                <option key={`top-city-${cityName}`} value={cityName}>{cityName}</option>
              ))}
            </select>
            <button
              onClick={detectCurrentLocation}
              disabled={locatingCity}
              style={{
                padding: "6px 8px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.25)",
                background: "#121827",
                color: "#dbeafe",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {locatingCity ? "Locating..." : "Detect"}
            </button>
          </div>

          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "nowrap",
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => onConsultVetNow?.()}
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                border: `1px solid ${PRIMARY_BLUE}`,
                background: "linear-gradient(135deg, #FFF8DC 0%, #F5C518 100%)",
                color: PRIMARY_INK,
                fontWeight: 700,
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
            >
              Consult Vet
            </button>
            {!user ? (
              <>
                <button
                  onClick={() => onLoginClick()}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,0.25)",
                    background: "transparent",
                    color: "#e2e8f0",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Login
                </button>
                <button
                  onClick={() => onSignupClick()}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 999,
                    border: `1px solid ${PRIMARY_BLUE}`,
                    background: "linear-gradient(135deg, #FFF8DC 0%, #F5C518 100%)",
                    color: PRIMARY_INK,
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Signup
                </button>
              </>
            ) : (
              <span style={{ color: "#cbd5e1", fontWeight: 600, fontSize: 13 }}>
                {String(user.email || "").slice(0, 24)}{String(user.email || "").length > 24 ? "..." : ""} | Saved {favoriteIds.size}
              </span>
            )}
          </div>
        </div>

        <div
          style={{
            background: "#32343b",
            borderBottomLeftRadius: 14,
            borderBottomRightRadius: 14,
            padding: "10px 12px",
            display: "flex",
            gap: 8,
            alignItems: "center",
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          {topRibbonTabs.map((tab) => (
            <button
              key={`top-tab-${tab.label}`}
              onClick={tab.onClick}
              style={{
                border: "none",
                background: "transparent",
                color: "#f8fafc",
                fontWeight: 600,
                fontSize: 18,
                padding: "6px 10px",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              {tab.label}
              {(tab.label === "Cats" || tab.label === "Dogs" || tab.label === "Pharmacy" || tab.label === "Shop By Breed") ? " ▾" : ""}
            </button>
          ))}

          <button
            onClick={() => onVetJoinClick?.()}
            style={{
              border: "1px solid rgba(255,255,255,0.22)",
              background: "#171a22",
              color: "#f8fafc",
              padding: "7px 12px",
              borderRadius: 999,
              fontWeight: 700,
              cursor: "pointer",
              marginLeft: "auto",
            }}
          >
            {role === "provider" ? "Vet Dashboard" : "For Vets"}
          </button>

          <div
            onMouseEnter={() => {
              if (!isCompactMegaMenu) openCategoryMenu();
            }}
            onMouseLeave={() => {
              if (!isCompactMegaMenu) closeCategoryMenu();
            }}
            style={{ position: "relative", marginLeft: 6 }}
            ref={categoryMenuAnchorRef}
          >
            <button
              onClick={toggleCategoryMenu}
              aria-expanded={showCategoryMenu}
              style={{
                padding: "8px 14px",
                fontWeight: 700,
                borderRadius: 999,
                border: `1px solid ${PRIMARY_BLUE}`,
                background: "linear-gradient(135deg, #FFF8DC 0%, #F5C518 100%)",
                color: PRIMARY_INK,
                cursor: "pointer",
              }}
            >
              All Services ▾
            </button>
            {showCategoryMenu && (
              <div
                ref={categoryMenuPanelRef}
                style={{
                  position: isCompactMegaMenu ? "fixed" : "absolute",
                  top: isCompactMegaMenu ? categoryMenuTop : "calc(100% + 6px)",
                  right: isCompactMegaMenu ? "auto" : 0,
                  left: isCompactMegaMenu ? "50%" : "auto",
                  transform: isCompactMegaMenu ? "translateX(-50%)" : "none",
                  width: isCompactMegaMenu ? "min(980px, calc(100vw - 24px))" : 980,
                  maxWidth: isCompactMegaMenu ? "calc(100vw - 24px)" : "92vw",
                  maxHeight: isCompactMegaMenu ? "70vh" : "none",
                  overflowY: isCompactMegaMenu ? "auto" : "visible",
                  background: "#fffdf5",
                  boxShadow: "0 18px 34px rgba(143, 111, 0, 0.18)",
                  borderRadius: 12,
                  border: "1px solid #F0E8C8",
                  zIndex: 120,
                  padding: 18,
                }}
                onMouseEnter={() => {
                  if (!isCompactMegaMenu) openCategoryMenu();
                }}
                onMouseLeave={() => {
                  if (!isCompactMegaMenu) closeCategoryMenu();
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isCompactMegaMenu
                      ? "1fr"
                      : "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: 14,
                  }}
                >
                  {serviceMegaColumns.map((column) => (
                    <div
                      key={column.title}
                      style={{
                        padding: "0 10px",
                        borderRight: isCompactMegaMenu ? "none" : "1px solid rgba(240, 232, 200, 0.9)",
                      }}
                    >
                      <div style={{ color: PRIMARY_INK, marginBottom: 10, fontSize: 18 }}>{column.title}</div>
                      <div style={{ display: "grid", gap: 10 }}>
                        {column.categories.map((cat) => (
                          <button
                            key={`nav-${cat.id}`}
                            onClick={() => {
                              applyQuickCategory(cat.name);
                              closeCategoryMenuImmediate();
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              width: "100%",
                              textAlign: "left",
                              border: "none",
                              background: "transparent",
                              color: HEADING_COLOR,
                              padding: 0,
                              cursor: "pointer",
                              fontWeight: 700,
                              fontSize: 15,
                            }}
                          >
                            <CategoryIconBadge
                              categoryName={cat.name}
                              size={22}
                              badgeSize={36}
                              background="#fff8db"
                            />
                            <span>{cat.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      <section
        className="petaar-coming-soon-banner petaar-coming-soon-banner--top"
        aria-label="Upcoming services announcement"
      >
        <div className="petaar-coming-soon-banner__copy">
          <span className="petaar-coming-soon-banner__eyebrow">Onboarding Now</span>
          <p className="petaar-coming-soon-banner__title">🐾 Something Pawsome is Coming Soon!</p>
          <p className="petaar-coming-soon-banner__text">
            We’re onboarding the best service providers near you to make pet care effortless.
          </p>
          <p className="petaar-coming-soon-banner__text petaar-coming-soon-banner__text--strong">
            Hold on tight—your pet deserves this!
          </p>
        </div>
      </section>

      {(hasGeo || nearMeOnly) && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff8db",
            border: "1px solid #f2d45e",
            borderRadius: 10,
            padding: "8px 12px",
            color: PRIMARY_INK,
            fontWeight: 600,
          }}
        >
          {hasGeo ?
            `Location active${nearestCityByCoords?.city ? ` near ${nearestCityByCoords.city}` : ""}.` :
            "Near-me mode is enabled."}
          {nearMeOnly ? ` Showing services within ${radiusKm} km.` : " Turn on Near Me to filter by distance."}
        </div>
      )}

      <section className="hero">
        <div
          className="hero-bg-image"
          style={{backgroundImage: `url(${HOME_HERO_IMAGE})`}}
        />
        <div className="hero-overlay" />
        <div className="hero-content-shell">
          <div className="hero-text-content">
            <span className="hero-eyebrow">🐾 Your Pets AI Yaar</span>
            {isMobileView ? (
              <div
                className="petaar-coming-soon-banner petaar-coming-soon-banner--mobile-hero"
                aria-label="Upcoming services announcement"
              >
                <div className="petaar-coming-soon-banner__copy">
                  <span className="petaar-coming-soon-banner__eyebrow">Onboarding Now</span>
                  <p className="petaar-coming-soon-banner__title">🐾 Something Pawsome is Coming Soon!</p>
                  <p className="petaar-coming-soon-banner__text">
                    We’re onboarding the best service providers near you to make pet care effortless.
                  </p>
                  <p className="petaar-coming-soon-banner__text petaar-coming-soon-banner__text--strong">
                    Hold on tight—your pet deserves this!
                  </p>
                </div>
              </div>
            ) : null}
            <h1>
              Find The Best
              <br />
              Pet Services Nearby
            </h1>
            <p>Best &amp; verified pet services across pan-India</p>
            <div className="hero-details-strip">
              <div className="strip-item">
                <strong>📍 Pan-India</strong>
                <span>Bengaluru · Mumbai · Delhi</span>
              </div>
              <div className="strip-divider" />
              <div className="strip-item">
                <strong>✅ 40+ Services</strong>
                <span>Grooming, Vet, Boarding &amp; more</span>
              </div>
              <div className="strip-divider" />
              <div className="strip-item">
                <strong>📹 24x7 Vet Consult</strong>
                <span>Avg. 3 min wait</span>
              </div>
              <div className="strip-divider" />
              <div className="strip-item">
                <strong>🛒 Marketplace</strong>
                <span>Food, meds &amp; accessories</span>
              </div>
            </div>
          </div>

          <div
            className="petaar-card petaar-home-vet-banner hero-vet-banner"
            style={{
              padding: isMobileView ? "16px 14px" : "24px 24px",
              display: "grid",
              gridTemplateColumns: viewportWidth < 900 ? "1fr" : "minmax(260px, 1fr) auto",
              gap: isMobileView ? 12 : 14,
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: isMobileView ? 8 : 10, marginBottom: 8, flexWrap: "wrap" }}>
                <CategoryIconBadge
                  categoryName="Veterinary & Health Care"
                  size={isMobileView ? 24 : 28}
                  badgeSize={isMobileView ? 46 : 52}
                  background="radial-gradient(circle at 30% 25%, #fffef0 0%, #ffe9a8 40%, #ffe066 72%, #f5c518 100%)"
                />
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    border: "1px solid rgba(245, 197, 24, 0.55)",
                    borderRadius: 999,
                    background: "rgba(255, 253, 240, 0.8)",
                    color: PRIMARY_INK,
                    fontWeight: 800,
                    fontSize: isMobileView ? 11 : 13,
                    padding: isMobileView ? "4px 9px" : "4px 10px",
                  }}
                >
                  🎥 Video Vet
                </span>
              </div>
              <div
                style={{
                  fontWeight: 800,
                  color: PRIMARY_INK,
                  marginBottom: 6,
                  fontSize: isMobileView ? 18 : 30,
                  lineHeight: 1.14,
                }}
              >
                Instant Video Consult with Vet
              </div>
              <div style={{ color: PRIMARY_INK, fontSize: isMobileView ? 13.5 : 17 }}>
                Connect with verified online vets now for urgent guidance, treatment advice, and next-step care.
              </div>
              <div style={{ marginTop: 10, display: "flex", gap: isMobileView ? 6 : 8, flexWrap: "wrap" }}>
                {["24x7 support", "Verified vets", "Digital prescription"].map((tag) => (
                  <span
                    key={tag}
                    style={{
                      border: "1px solid rgba(245, 197, 24, 0.55)",
                      background: "rgba(255, 253, 240, 0.85)",
                      color: PRIMARY_INK,
                      borderRadius: 999,
                      padding: isMobileView ? "4px 9px" : "4px 10px",
                      fontSize: isMobileView ? 11 : 12,
                      fontWeight: 700,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: isMobileView ? "flex-start" : "flex-end",
                width: "100%",
              }}
            >
              <button
                onClick={() => onConsultVetNow?.()}
                style={{
                  ...PRIMARY_BUTTON_STYLE,
                  fontSize: isMobileView ? 13 : 15,
                  width: isMobileView ? "100%" : "auto",
                  flex: isMobileView ? "1 1 100%" : "0 0 auto",
                  justifyContent: "center",
                }}
              >
                Start Video Consult
              </button>
              <button
                onClick={() => onConsultVetNow?.()}
                style={{
                  ...SECONDARY_BUTTON_STYLE,
                  fontSize: isMobileView ? 13 : 15,
                  width: isMobileView ? "100%" : "auto",
                  flex: isMobileView ? "1 1 100%" : "0 0 auto",
                  justifyContent: "center",
                }}
              >
                View Online Vets
              </button>
            </div>
          </div>

        </div>
      </section>

      <div style={{ marginBottom: 16 }}>
        <small style={{ color: "#64748b", fontWeight: 600 }}>Featured Cities</small>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
          {TOP_CITIES.map((cityName, idx) => {
            const displayCity = toDisplayCity(cityName);
            const cityIcon = cityMonumentIcons[displayCity] || getCityFallbackIcon(displayCity, idx);
            const isActive = city === cityName;
            return (
            <button
              key={cityName}
              onClick={() => {
                setCity(cityName);
                setTimeout(() => scrollToServices(), 60);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                borderRadius: 999,
                border: isActive ? "1px solid #2563eb" : "1px solid #cbd5e1",
                background: isActive ? "#eff6ff" : "#fff",
                padding: "6px 10px 6px 6px",
                fontWeight: 700,
                color: "#1f2937",
                cursor: "pointer",
                transition: "all 120ms ease",
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.18)",
                  flex: "0 0 28px",
                  border: "1px solid rgba(255,255,255,0.9)",
                  overflow: "hidden",
                  background: "#e2e8f0",
                }}
              >
                <img
                  src={cityIcon}
                  alt={`${displayCity} monument`}
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.currentTarget.src = getCityFallbackIcon(displayCity, idx);
                  }}
                />
              </span>
              <span>{displayCity}</span>
            </button>
            );
          })}
        </div>
      </div>

      <section
        className="petaar-card"
        style={{
          marginBottom: 16,
          padding: isMobileView ? 12 : 14,
          display: "grid",
          gridAutoFlow: "column",
          gridAutoColumns: isMobileView ? "minmax(150px, 1fr)" : "1fr",
          overflowX: isMobileView ? "auto" : "visible",
          gap: 10,
        }}
      >
        {[
          { icon: "🐾", value: "500+", label: "Services Listed" },
          { icon: "✅", value: "200+", label: "Verified Providers" },
          { icon: "🏙️", value: "10+", label: "Cities" },
          { icon: "⭐", value: "4.8", label: "Average Rating" },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              minWidth: isMobileView ? 140 : "auto",
              border: "1px solid #dbe3ee",
              borderRadius: 16,
              padding: 12,
              background: "#fff",
              textAlign: "center",
            }}
          >
            <div style={{ color: PRIMARY_BLUE, fontSize: 18, marginBottom: 4 }}>{item.icon}</div>
            <div style={{ color: HEADING_COLOR, fontSize: 18, fontWeight: 800 }}>{item.value}</div>
            <div style={{ color: META_COLOR, fontSize: 12, fontWeight: 600 }}>{item.label}</div>
          </div>
        ))}
      </section>

      <div
        className="petaar-card"
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: isMobileView ? 24 : 30, lineHeight: 1.12 }}>Everything Your Pet Needs - All in One Place</h2>
          <button
            onClick={() => onOpenMarketplace?.()}
            style={{
              border: `1px solid ${PRIMARY_BLUE}`,
              background: "linear-gradient(135deg, #FFF8DC 0%, #F5C518 100%)",
              color: PRIMARY_INK,
              borderRadius: 999,
              padding: isMobileView ? "7px 12px" : "8px 14px",
              fontWeight: 700,
              fontSize: isMobileView ? 13 : 14,
              cursor: "pointer",
            }}
          >
            Open Marketplace
          </button>
        </div>
        <p style={{ marginTop: 6, color: "#6b7280", fontSize: isMobileView ? 13 : 16 }}>
          Browse 40+ premium pet services designed for pan-India pet parents.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${isMobileView ? 148 : 210}px, 1fr))`,
            gap: isMobileView ? 8 : 10,
            marginTop: 12,
          }}
        >
          {SERVICE_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => {
                if (typeof onOpenCategoryPage === "function") {
                  onOpenCategoryPage(cat.name);
                  return;
                }
                applyQuickCategory(cat.name, { clearCity: true, clearSearch: true, autoScroll: true });
              }}
              className={`petaar-home-category-card${selectedCategory === cat.name ? " is-selected" : ""}`}
              style={{
                textAlign: "left",
                padding: isMobileView ? 10 : 14,
                cursor: "pointer",
              }}
            >
              <CategoryIconBadge categoryName={cat.name} size={isMobileView ? 42 : 60} badgeSize={isMobileView ? 68 : 98} />
              <strong style={{ display: "block", marginTop: isMobileView ? 8 : 10, fontSize: isMobileView ? 16 : 18, lineHeight: 1.2 }}>{cat.name}</strong>
              <small style={{ color: META_COLOR, fontSize: isMobileView ? 11 : 13 }}>
                {formatServiceCount(cat.services.length, cat.id)}
              </small>
            </button>
          ))}
        </div>
      </div>

      {onOpenLanding && (
        <section
          className="petaar-card"
          style={{ marginBottom: 16 }}
        >
          <h3 style={{ margin: "0 0 10px", fontSize: 20, color: HEADING_COLOR }}>Featured Services</h3>
          <div style={{ display: "grid", gap: 0 }}>
            {landingHighlights.map((item, idx) => (
              <button
                key={`landing-highlight-${idx}-${item.city}-${item.category}`}
                onClick={() => onOpenLanding({ city: item.city, category: item.category })}
                style={{
                  textAlign: "left",
                  border: "none",
                  borderBottom: idx < landingHighlights.length - 1 ? "1px solid #EEEEEE" : "none",
                  borderRadius: 0,
                  background: "#fff",
                  padding: "14px 8px",
                  cursor: "pointer",
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 12,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = "#F0F4FF";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "#fff";
                }}
              >
                <CategoryIconBadge categoryName={item.category} size={22} badgeSize={40} />
                <strong style={{ display: "block", color: "#0f172a", fontSize: 16 }}>
                  {item.category}
                </strong>
                <span style={{ color: "#64748b", fontSize: 24, lineHeight: 1 }}>›</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobileView ? "1fr" : "1fr auto",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
          background: "linear-gradient(90deg, #1B3DB5 0%, #4A6FE3 100%)",
          borderRadius: 16,
          padding: isMobileView ? "14px 12px" : "14px 18px",
        }}
      >
        <div style={{ color: "#fff", fontWeight: 800 }}>
          🐾 Download app & get ₹200 pet health cash
        </div>
        <button
          style={{
            ...SECONDARY_BUTTON_STYLE,
            background: "#fff",
            color: PRIMARY_BLUE,
            borderColor: "#fff",
            padding: "10px 18px",
          }}
        >
          Download Now
        </button>
      </div>

      <section
        className="petaar-card"
        style={{
          marginBottom: 18,
          background: "linear-gradient(120deg, #fff4e6 0%, #f4f0ff 55%, #eaf4ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 8,
            borderRadius: 16,
            border: "2px solid rgba(255,255,255,0.8)",
            pointerEvents: "none",
          }}
        />
        <h3 style={{ margin: "0 0 14px", textAlign: "center", color: "#1f2a4d", fontSize: 22 }}>
          How Petaar Works
          <span style={{ marginLeft: 8 }}>🐾</span>
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
          }}
        >
          {[
            { title: "Search by city", text: "Choose your city and category to discover nearby providers." },
            { title: "Compare providers", text: "Check profile, photos, ratings, and verified trust badges." },
            { title: "Book instantly", text: "Pay securely and track your booking from dashboard." },
            { title: "Get AI support", text: "Use Petaar AI chat for care tips and booking guidance." },
          ].map((item, index) => (
            <div
              key={item.title}
              style={{
                border: "1px solid #dbe3ee",
                borderRadius: 16,
                background: "#f8fbff",
                padding: 16,
                boxShadow: "0 10px 20px rgba(15,23,42,0.08)",
                display: "grid",
                gap: 8,
                alignContent: "start",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    background: "#dbeafe",
                    border: `1px solid ${PRIMARY_BLUE}55`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: PRIMARY_BLUE,
                    fontWeight: 800,
                    fontSize: 18,
                  }}
                >
                  {index + 1}
                </div>
                <strong>{item.title}</strong>
              </div>
              <p style={{ margin: 0, color: "#475569", fontSize: 14 }}>{item.text}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            onClick={() => {
              setSearch("");
              setCategory("");
              setSelectedCategory("");
              setTimeout(() => scrollToServices(), 60);
            }}
            style={PRIMARY_BUTTON_STYLE}
          >
            Get Started
          </button>
        </div>
      </section>

      {user && (
        <section
          className="petaar-card"
          style={{ marginBottom: 18 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0 }}>Saved Services</h3>
            <button
              onClick={() => setSavedOnly((prev) => !prev)}
              style={savedOnly ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE}
            >
              {savedOnly ? "Showing Saved Only" : "Show Saved Only"}
            </button>
          </div>
          {favoritesLoading ? (
            <p style={{ marginBottom: 0, color: "#64748b" }}>Loading saved services...</p>
          ) : favoriteServices.length === 0 ? (
            <p style={{ marginBottom: 0, color: "#64748b" }}>
              Save services to shortlist providers and book faster later.
            </p>
          ) : (
            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 10,
              }}
            >
              {favoriteServices.map((service) => (
                <div key={`fav-${service.id}`} style={{ border: "1px solid #dbe3ee", borderRadius: 16, padding: 16, background: "#fff" }}>
                  <strong style={{ display: "block", marginBottom: 4 }}>{service.title}</strong>
                  <small style={{ color: "#475569" }}>
                    {service.city} | ₹{service.price}
                    {formatDistance(service.distanceKm) ? ` | ${formatDistance(service.distanceKm)}` : ""}
                  </small>
                  <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={() => openProviderWithTracking(service)} style={SECONDARY_BUTTON_STYLE}>View Details</button>
                    <RazorpayButton
                      service={service}
                      selectedPet={selectedPet}
                      blockedDates={Array.isArray(service?.unavailableDates) ? service.unavailableDates : []}
                      blockedSlots={Array.isArray(service?.unavailableSlots) ? service.unavailableSlots : []}
                      buttonLabel="Book Now"
                      buttonStyle={PRIMARY_BUTTON_STYLE}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {recentServices.length > 0 && (
        <section
          className="petaar-card"
          style={{ marginBottom: 18 }}
        >
          <h3 style={{ margin: "0 0 10px" }}>Recently Viewed</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 10,
            }}
          >
            {recentServices.map((service) => (
              <div key={`recent-${service.id}`} style={{ border: "1px solid #dbe3ee", borderRadius: 16, padding: 16, background: "#fff" }}>
                <strong style={{ display: "block", marginBottom: 4 }}>{service.title}</strong>
                <small style={{ color: "#475569" }}>
                  {service.city} | ₹{service.price}
                  {formatDistance(service.distanceKm) ? ` | ${formatDistance(service.distanceKm)}` : ""}
                </small>
                <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => openProviderWithTracking(service)} style={SECONDARY_BUTTON_STYLE}>View Details</button>
                  <RazorpayButton
                    service={service}
                    selectedPet={selectedPet}
                    blockedDates={Array.isArray(service?.unavailableDates) ? service.unavailableDates : []}
                    blockedSlots={Array.isArray(service?.unavailableSlots) ? service.unavailableSlots : []}
                    buttonLabel="Book Now"
                    buttonStyle={PRIMARY_BUTTON_STYLE}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeCategory && quickTiles.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 8 }}>{activeCategory} - Top Services</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            {quickTiles.map((tile, idx) => (
              <div
                key={`${tile}-${idx}`}
                style={{
                  border: "1px solid #dbe3ee",
                  borderRadius: 16,
                  background: "#fff",
                  padding: 16,
                }}
              >
                <strong style={{ display: "block", minHeight: 44 }}>{tile}</strong>
                <small style={{ color: "#64748b" }}>Starts ₹{299 + idx * 80}</small>
                <div>
                  <button
                    style={{ ...PRIMARY_BUTTON_STYLE, marginTop: 8 }}
                    onClick={() => setSearch(tile)}
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 ref={servicesRef} style={{ marginBottom: 8 }}>
        Available Services ({displayServices.length})
      </h2>
      {!loading && displayServices.length > 0 && (
        <p style={{ marginTop: 0, color: "#475569" }}>
          Showing {((servicesPage - 1) * servicesPageSize) + 1}-
          {Math.min(servicesPage * servicesPageSize, displayServices.length)} of {displayServices.length}
        </p>
      )}
      {loading && <p>Loading live services...</p>}
      {loadError && (
        <div
          style={{
            marginTop: 8,
            marginBottom: 12,
            padding: 12,
            borderRadius: 8,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            color: "#991b1b",
          }}
        >
          Could not load live services: {loadError}
        </div>
      )}
      {!loading && pagedDisplayServices.map((service) => {
        const reviewMeta = getReviewMeta(service);
        const categoryName = service.normalizedCategory || service.category;
        const heroImage =
          (Array.isArray(service.imageUrls) && service.imageUrls[0]) ||
          CATEGORY_ICON_IMAGES[categoryName] ||
          "/petaar-logo.jpeg";
        return (
          <div
            key={service.id}
            style={{
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              padding: 16,
              marginTop: 15,
              borderRadius: 16,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              position: "relative",
            }}
          >
            <button
              onClick={() => toggleFavoriteService(service)}
              disabled={favoriteBusyId === service.id}
              title="Save service"
              style={{
                ...SMALL_ICON_BUTTON_STYLE,
                position: "absolute",
                top: 12,
                right: 12,
                opacity: favoriteBusyId === service.id ? 0.6 : 1,
              }}
            >
              {isFavoriteService(service.id) ? "♥" : "♡"}
            </button>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobileView ? "1fr" : "auto 1fr",
                gap: 14,
                alignItems: "start",
                paddingRight: 32,
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: "#e8f1ff",
                  border: "2px solid #fff",
                  boxShadow: "0 8px 16px rgba(30, 58, 138, 0.14)",
                }}
              >
                <img
                  src={heroImage}
                  alt={service.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  <span
                    style={{
                      border: "1px solid #a7f3d0",
                      color: "#047857",
                      background: "#ecfdf5",
                      borderRadius: 999,
                      padding: "2px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    ✓ Verified
                  </span>
                  <span style={{ color: META_COLOR, fontSize: 13, fontWeight: 700 }}>
                    ★ {reviewMeta.avg.toFixed(1)} ({reviewMeta.count} reviews)
                  </span>
                </div>

                <h3 style={{ margin: "0 0 6px", fontSize: 18, color: HEADING_COLOR }}>
                  {service.title}
                  {service.isFallback && (
                    <small style={{ color: "#64748b", marginLeft: 8 }}>(Sample)</small>
                  )}
                </h3>

                <div style={{ color: META_COLOR, fontSize: 13, marginBottom: 8 }}>
                  {categoryName} | {service.city} | {service.availabilityNote || "Mon-Sat"}
                </div>

                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 999,
                    background: "#E8EEFF",
                    color: PRIMARY_BLUE,
                    padding: "6px 12px",
                    fontWeight: 700,
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  Starting from ₹{service.price}
                </span>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {service.isFallback ? (
                    <button
                      onClick={() => onConsultVetNow?.()}
                      style={PRIMARY_BUTTON_STYLE}
                    >
                      Book Now
                    </button>
                  ) : user ? (
                    <RazorpayButton
                      service={service}
                      selectedPet={selectedPet}
                      blockedDates={Array.isArray(service?.unavailableDates) ? service.unavailableDates : []}
                      blockedSlots={Array.isArray(service?.unavailableSlots) ? service.unavailableSlots : []}
                      buttonLabel="Book Now"
                      buttonStyle={PRIMARY_BUTTON_STYLE}
                    />
                  ) : (
                    <button
                      onClick={() => onLoginClick(service)}
                      style={PRIMARY_BUTTON_STYLE}
                    >
                      Book Now
                    </button>
                  )}
                  <button
                    onClick={() => openProviderWithTracking(service)}
                    style={SECONDARY_BUTTON_STYLE}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {!loading && displayServices.length > 0 && servicesTotalPages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
          <button onClick={() => setServicesPage(1)} disabled={servicesPage <= 1} style={SECONDARY_BUTTON_STYLE}>First</button>
          <button onClick={() => setServicesPage((prev) => Math.max(1, prev - 1))} disabled={servicesPage <= 1} style={SECONDARY_BUTTON_STYLE}>
            Previous
          </button>
          <span>Page {servicesPage} of {servicesTotalPages}</span>
          <button
            onClick={() => setServicesPage((prev) => Math.min(servicesTotalPages, prev + 1))}
            disabled={servicesPage >= servicesTotalPages}
            style={SECONDARY_BUTTON_STYLE}
          >
            Next
          </button>
          <button onClick={() => setServicesPage(servicesTotalPages)} disabled={servicesPage >= servicesTotalPages} style={SECONDARY_BUTTON_STYLE}>
            Last
          </button>
        </div>
      )}

      {displayServices.length === 0 && (
        <div style={{ marginTop: 24, color: "#666" }}>
          <p style={{ marginTop: 0 }}>No services found for selected filters.</p>
          <button
            onClick={() => {
              setSearch("");
              setCity("");
            setCategory("");
            setSelectedCategory("");
            setMaxPrice("");
            setNearMeOnly(false);
            setRadiusKm("50");
          }}
          >
            Clear Filters
          </button>
        </div>
      )}

      {showCityShowcase && (
        <section
          style={{
            marginTop: 30,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: "24px 18px 16px",
          }}
        >
          <h2
            style={{
              margin: "0 0 18px",
              textAlign: "center",
              fontSize: "clamp(30px, 4.8vw, 52px)",
              lineHeight: 1.2,
              color: "#4b5563",
              fontWeight: 800,
            }}
          >
            Top-Quality Pet Services Now in Your City
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
              gap: 14,
            }}
          >
            {cityShowcase.map((cityName, idx) => {
              const displayCity = toDisplayCity(cityName);
              const cityIcon = cityMonumentIcons[displayCity] || getCityFallbackIcon(displayCity, idx);
              return (
                <button
                  key={`city-showcase-${cityName}`}
                  onClick={() => {
                    setCity(displayCity);
                    setTimeout(() => scrollToServices(), 60);
                  }}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: "8px 4px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      width: 86,
                      height: 86,
                      borderRadius: 22,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 16px rgba(15,23,42,0.14)",
                      border: "2px solid #fff",
                      overflow: "hidden",
                      background: "#e2e8f0",
                    }}
                  >
                    <img
                      src={cityIcon}
                      alt={`${displayCity} monument`}
                      loading="lazy"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.src = getCityFallbackIcon(displayCity, idx);
                      }}
                    />
                  </span>
                  <span style={{ marginTop: 8, fontWeight: 700, color: "#111827" }}>
                    {displayCity}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {!hideLocalChrome && (
        <footer
          style={{
            marginTop: 34,
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: 14,
            padding: "24px 20px 16px",
            color: "#111827",
          }}
        >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <button
            type="button"
            onClick={handleLogoHomeClick}
            aria-label="Go to home page"
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              display: "inline-flex",
              cursor: "pointer",
            }}
          >
            <img
              src="/petaar-logo.jpeg"
              alt="Petaar"
              style={{ height: 34, width: "auto", objectFit: "contain" }}
            />
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 20,
          }}
        >
          {footerColumns.map((col) => (
            <div key={`footer-col-${col.title}`}>
              <h4 style={{ margin: "0 0 10px" }}>{col.title}</h4>
              <div style={{ display: "grid", gap: 8 }}>
                {col.links.map((link) => (
                  <button
                    key={`footer-link-${col.title}-${link.label}`}
                    onClick={() => {
                      if (link.legalKey && onOpenLegal) {
                        onOpenLegal(link.legalKey);
                      }
                    }}
                    style={{
                      color: "#4b5563",
                      textDecoration: "none",
                      background: "transparent",
                      border: "none",
                      textAlign: "left",
                      padding: 0,
                      cursor: link.legalKey ? "pointer" : "default",
                    }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div>
            <h4 style={{ margin: "0 0 10px" }}>Social links</h4>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              {socialLinks.map((item) => (
                <a
                  key={`social-${item}`}
                  href="#/"
                  onClick={(e) => e.preventDefault()}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    border: "1px solid #d1d5db",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: 12,
                    background: "#fff",
                  }}
                >
                  {item}
                </a>
              ))}
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <button
                style={{
                  border: "none",
                  borderRadius: 8,
                  background: "#111827",
                  color: "#fff",
                  padding: "8px 12px",
                  width: 160,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                Download on App Store
              </button>
              <button
                style={{
                  border: "none",
                  borderRadius: 8,
                  background: "#111827",
                  color: "#fff",
                  padding: "8px 12px",
                  width: 160,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                Get it on Google Play
              </button>
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 18, paddingTop: 12, color: "#6b7280", fontSize: 13 }}>
          <div>* As of February 18, 2026</div>
          <div style={{ marginTop: 6 }}>
            Copyright 2026 Petaar Technologies. All rights reserved.
          </div>
        </div>
        </footer>
      )}

      {!hideLocalAi && (
        <PetAIAssistant
          services={normalizedServices}
          onOpenProvider={onOpenProvider}
          onApplyBookingIntent={applyAiBookingIntent}
          user={user}
          selectedPet={selectedPet}
          onHome={handleLogoHomeClick}
        />
      )}
    </div>
  );
}
